import React from 'react';
import styles from './index.module.scss';
import classNames from 'classnames';
import updateImageListSrc from "@utils/update-image-list-src";
import { BASE_IMG_URL, livePhotoImage } from '@images/imageUrls';
import { getProtectedEventHandlers, isContentProtectionEnabled, protectedMediaStyle } from '@utils/contentProtection';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
// 导入 Swiper 样式
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Image, Spin } from 'antd';
import LivePhotoPreview from '@components/livePhotoPreview';
// 比例计算工具函数
const calculateAspectRatio = (width, height) => {
  return width / height;
};
// 为你的图片样式明确定义类型
interface ImageStyles {
  width: string;
  height: string;
  objectFit: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
}

// 安全比例判断函数
const getSafeAspectRatio = (naturalRatio) => {
  const MIN_PORTRAIT = 3/4;   // 0.75
  const MAX_LANDSCAPE = 4/3;  // 1.333
  
  // 如果比例在安全范围内，使用原比例
  if (naturalRatio >= MIN_PORTRAIT && naturalRatio <= MAX_LANDSCAPE) {
    return naturalRatio;
  }
  
  // 否则使用强制安全比例
  return naturalRatio < 1 ? MIN_PORTRAIT : MAX_LANDSCAPE;
};
interface H5ThreadDetailImgListProps {
  imageList: any
  className?: any
  allowDownload?: boolean
  isVivoBrowserStatus?: any
}
interface H5ThreadDetailImgListState {
  containerAspectRatio: any
  containerType: any
  activeIndex: any
  isInitialized: any
  previewVisible: boolean
  previewIndex: number
}
class H5ThreadDetailImgList extends React.Component<H5ThreadDetailImgListProps, H5ThreadDetailImgListState> {
  constructor(props) {
    super(props);
    this.state = {
      containerAspectRatio: null,
      containerType: 'landscape', // 'portrait' 或 'landscape'
      activeIndex: 0,
      isInitialized: false,
      previewVisible: false,
      previewIndex: 0
    };
  }

  componentDidMount() {
    this.initializeCarousel();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.imageList !== this.props.imageList) {
      this.initializeCarousel();
    }
  }

  // 初始化轮播图比例
  initializeCarousel = () => {
    const { imageList } = this.props;
    
    if (!imageList || imageList.length === 0) {
      this.setState({ 
        containerAspectRatio: 4/3,
        containerType: 'landscape',
        isInitialized: true
      });
      return;
    }

    // 获取第一张图片的宽高信息
    const firstImage = imageList[0];
    if (firstImage.fileWidth && firstImage.fileHeight) {
      const naturalRatio = calculateAspectRatio(firstImage.fileWidth, firstImage.fileHeight);
      const safeRatio = getSafeAspectRatio(naturalRatio);
      const containerType = safeRatio < 1 ? 'portrait' : 'landscape';
      
      this.setState({
        containerAspectRatio: safeRatio,
        containerType: containerType,
        isInitialized: true
      });
    } else {
      // 如果没有宽高信息，使用默认比例
      this.setState({
        containerAspectRatio: 4/3,
        containerType: 'landscape',
        isInitialized: true
      });
    }
  };

  // 处理图片加载错误
  handleImageError = (index) => {
    console.warn(`图片 ${index + 1} 加载失败`);
  };

  // 滑动变化回调
  handleSlideChange = (swiper) => {
    this.setState({ activeIndex: swiper.activeIndex });
  };

  // 获取图片显示样式
  getImageStyle = (): React.CSSProperties => {
    const { containerType } = this.state;
  
    if (containerType === 'portrait') {
      return { 
        width: 'auto', 
        height: '100%',
        objectFit: 'contain' as const, // 使用 const 断言
        display: 'block',
        margin: 'auto'
      };
    } else {
      return { 
        width: '100%', 
        height: 'auto',
        objectFit: 'contain' as const, // 使用 const 断言
        display: 'block',
        margin: 'auto'
      };
    }
  };

  // 打开全屏预览
  openPreview = (index: number) => {
    this.setState({ previewVisible: true, previewIndex: index });
  };

  // 关闭全屏预览
  closePreview = () => {
    this.setState({ previewVisible: false });
  };

  render() {
    const { imageList, className = '', allowDownload, isVivoBrowserStatus } = this.props;
    const { containerAspectRatio, containerType, isInitialized, activeIndex, previewVisible, previewIndex } = this.state;
    const isProtected = isContentProtectionEnabled(allowDownload);
    const protectedHandlers = getProtectedEventHandlers(isProtected);

    if (!imageList || imageList.length === 0) {
      return (
        <div className={classNames(styles.imageCarousel, styles.empty, `${className}`)}>
          <div className={classNames(styles.carouselPlaceholder)}>暂无图片</div>
        </div>
      );
    }

    if (!isInitialized) {
      return (
        <div className={classNames(styles.imageCarousel, styles.loading, `${className}`)}>
          <div className={classNames(styles.carouselLoading)}>加载中...</div>
        </div>
      );
    }

    const containerStyle = {
      aspectRatio: `${containerAspectRatio}`,
      // 使用 flexbox 实现垂直居中
      display: 'flex',
      alignItems: 'center',     // 垂直居中
      justifyContent: 'center', // 水平居中
      overflow: 'hidden',       // 防止溢出
      ...(isProtected ? protectedMediaStyle : {})
    };

    return (<div className={styles.imageListWrap}>
        <div className={classNames(styles.imageCarousel, styles.loading, `${className}`,  `${containerType}`)}>
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            autoplay={false}
            loop={false}
            onSlideChange={this.handleSlideChange}
            className={classNames(styles.carouselSwiper)}
          >
            {imageList.map((image, index) => (
              <SwiperSlide key={image.id || index}>
                <div 
                  className={classNames(styles.slideContainer, isProtected && styles.protectedSlideContainer)}
                  style={containerStyle}
                  {...protectedHandlers}
                  onClick={() => {!isVivoBrowserStatus && this.openPreview(index)}}
                >
                  <Image
                      src={updateImageListSrc(image.url || image.webpUrl, 7)}
                      alt={image.fileName || `轮播图 ${index + 1}`}
                      className={classNames(styles.carouselImage, index === activeIndex ? styles.active : '')}
                      style={{
                        ...this.getImageStyle(),
                        ...(isProtected ? protectedMediaStyle : {})
                      }}
                      onError={() => this.handleImageError(index)}
                      loading={index === 0 ? 'eager' : 'lazy'}
                      fallback={`${BASE_IMG_URL}/common/pic_place1.png`}
                      preview={false}
                  />
                  {isProtected && <div className={styles.protectedOverlay} onContextMenu={e => e.preventDefault()} />}
                  {/* Live 图标识 */}
                  {image.isLive && image.liveUrl && (
                    <div className={styles.liveIndicator}>
                      <div className={styles.liveTag}>
                        <img className={styles.liveIcon} src={livePhotoImage.liveIcon} alt="" />
                        <span>动态照片</span>
                      </div>
                      <div className={styles.soundTag}>
                        <img className={styles.soundIcon} src={livePhotoImage.volumeClose} alt="" />
                      </div>
                    </div>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <div className={styles.customPagination1}>
              <div className={`swiper-pagination ${styles.customPagination}`}></div>
              <div className={styles.paginationInfo}>
                  <span className={styles.currentIndex}>{activeIndex + 1}</span>
                  <span className={styles.slash}>/</span>
                  <span className={styles.totalCount}>{imageList?.length}</span>
              </div>
          </div>

          <div className={styles.customPagination2}>
              {imageList?.map((img: any, index: any) => (
                  <div className={classNames(styles.pageItem, activeIndex == index ? styles.swiperActive : '')}></div>
              ))}
          </div>

          {/* 全屏图片/Live图预览 */}
          <LivePhotoPreview
            visible={previewVisible}
            imageList={imageList}
            initialIndex={previewIndex}
            onClose={this.closePreview}
            allowDownload={allowDownload}
          />
      </div>
    );
  }
}

export default H5ThreadDetailImgList;
